import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Building2,
  Key,
  Zap,
  Users,
  BarChart2,
  ArrowRight,
  Code2,
  Search,
} from "lucide-react";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import Footer from "../../components/Footer";
import api from "../../api/client";

export default function EmployerLanding() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "Joborigo for Employers: Stop ghosting. Start hiring.";
    api
      .get("/public/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10 sticky top-0 z-30 bg-white dark:bg-brand-dark-bg">
        <Logo className="h-12" />
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <Link
            to="/employers/agency"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            For agencies →
          </Link>
          <Link
            to="/employers/login"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            Sign in
          </Link>
          <Link
            to="/employers/register"
            className="text-sm bg-brand-violet text-white px-4 py-1.5 rounded-md hover:opacity-90 transition"
          >
            Get started free
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-brand-light-bg dark:bg-brand-dark-bg py-24 px-6 text-center">
        <p className="text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark mb-3 uppercase tracking-widest">
          For employers
        </p>
        <h1 className="text-5xl font-extrabold text-gray-900 dark:text-white mb-4 max-w-2xl mx-auto leading-tight">
          Stop ghosting candidates.
          <br />
          Start building a reputation.
        </h1>
        <p className="text-gray-500 dark:text-gray-400 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
          Joborigo gives you a direct channel to candidates who are actively
          tracking your responsiveness. Push real-time status updates. Browse
          anonymous talent. Build the kind of employer brand that actually
          attracts people.
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <Link
            to="/employers/register"
            className="bg-brand-violet text-white px-8 py-3 rounded-lg text-lg font-medium hover:opacity-90 transition"
          >
            Get started free
          </Link>
          <Link
            to="/employers/api"
            className="bg-white dark:bg-brand-dark-surface border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 px-8 py-3 rounded-lg text-lg font-medium hover:border-brand-violet dark:hover:border-brand-violet-on-dark hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
          >
            View API docs
          </Link>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="bg-brand-violet dark:bg-brand-dark-surface py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-white/60 text-sm text-center mb-10 uppercase tracking-widest font-medium">
              Joborigo by the numbers
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
              {[
                {
                  value: stats.total_seekers,
                  label: "Job seekers tracking applications",
                },
                {
                  value: stats.total_applications,
                  label: "Applications being tracked",
                },
                {
                  value: stats.total_status_pushes,
                  label: "Real-time status updates pushed",
                },
                {
                  value: stats.total_employers,
                  label: "Employers on platform",
                },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-4xl font-bold text-white mb-1">
                    {s.value.toLocaleString()}
                  </p>
                  <p className="text-white/60 text-xs leading-relaxed">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What is Joborigo for employers */}
      <section className="py-20 px-6 bg-white dark:bg-brand-dark-bg">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
              What you get
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              Everything you need to hire without the friction
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-sm leading-relaxed">
              Joborigo connects directly to your ATS or HR system. Candidates
              who apply with a Joborigo connect link give you a direct channel
              to push updates, no email required.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                icon: Zap,
                title: "Real-time status updates",
                desc: "Push status changes directly from your ATS to candidates. Interview scheduled, offer extended, rejected. Candidates know immediately. No more silence.",
              },
              {
                icon: Search,
                title: "Browse anonymous candidates",
                desc: "Browse job seekers who have chosen to be found. No names, no gender, no photos. Just skills, availability, and salary expectations.",              },
              {
                icon: Key,
                title: "Privacy-preserving integration",
                desc: "Candidates share a one-time connect token instead of their email. Your ATS captures it and calls the Joborigo API. The candidate's identity stays protected.",
              },
              {
                icon: BarChart2,
                title: "Full process visibility",
                desc: "See the full hiring history for eevery connected candidate: status timeline, correspondence, and their responses to meetings and document requests.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 p-5 bg-gray-50 dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10"
              >
                <div className="w-9 h-9 rounded-xl bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                    {item.title}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-brand-dark-surface">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
              How it works
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              Up and running in minutes
            </h2>
          </div>
          <div className="flex flex-col gap-8">
            {[
              {
                step: "01",
                title: "Register your employer account",
                desc: "Create a free account at joborigo.com/employers/register. Generate an API key from your dashboard in seconds.",
              },
              {
                step: "02",
                title: "Candidates bring the connection",
                desc: "Job seekers paste their Joborigo connect link in the portfolio or website field when they apply. Your ATS captures the token automatically.",
              },
              {
                step: "03",
                title: "Call the integrate endpoint",
                desc: "One API call with the connect token links the candidate to your account. No email address required. The integration is permanent.",
              },
              {
                step: "04",
                title: "Push status updates as you go",
                desc: "Every time the candidate's status changes in your ATS, call the status-push endpoint. They see it immediately in their Joborigo dashboard.",
              },
            ].map((step) => (
              <div key={step.step} className="flex gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-brand-violet-tint dark:bg-brand-violet/20 flex items-center justify-center">
                  <span className="text-sm font-bold text-brand-violet dark:text-brand-violet-on-dark">
                    {step.step}
                  </span>
                </div>
                <div className="flex-1 pt-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {step.title}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API cards */}
      <section className="py-20 px-6 bg-white dark:bg-brand-dark-bg">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-brand-violet dark:text-brand-violet-on-dark uppercase tracking-widest mb-3">
              Developer docs
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-4">
              Two APIs. One integration.
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto text-sm leading-relaxed">
              The ATS API handles candidate linking and status updates. The
              Arena API lets you browse and reach out to candidates proactively.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              {
                icon: Code2,
                title: "ATS / Status push API",
                desc: "Link candidates via connect token, resume slug, or email. Push real-time status updates and structured correspondence actions.",
                link: "/employers/api",
                label: "View ATS API docs",
                badge: "For ATS integration",
                badgeColor:
                  "bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-400",
              },
              {
                icon: Search,
                title: "Arena API",
                desc: "Browse anonymous candidates, send job positions, track sent positions and candidate responses, view full process history.",
                link: "/employers/arena-api",
                label: "View Arena API docs",
                badge: "For proactive sourcing",
                badgeColor:
                  "bg-brand-violet-tint text-brand-violet dark:bg-brand-violet/20 dark:text-brand-violet-on-dark",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="flex flex-col p-6 bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm dark:shadow-none"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-brand-violet/10 flex items-center justify-center flex-shrink-0">
                    <card.icon className="w-5 h-5 text-brand-violet dark:text-brand-violet-on-dark" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {card.title}
                    </p>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${card.badgeColor}`}
                    >
                      {card.badge}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-4 flex-1">
                  {card.desc}
                </p>
                <Link
                  to={card.link}
                  className="flex items-center gap-2 text-sm font-medium text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  {card.label} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-brand-dark-surface">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-2xl bg-brand-violet p-px">
            <div className="rounded-2xl bg-white dark:bg-brand-dark-bg px-8 py-10 text-center">
              <h2 className="text-2xl font-bold mb-2">
                Ready to stop ghosting?
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 leading-relaxed">
                Create a free employer account and generate your first API key
                in minutes. No credit card required.
              </p>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Link
                  to="/employers/register"
                  className="bg-brand-violet text-white px-8 py-3 rounded-lg font-medium hover:opacity-90 transition"
                >
                  Create employer account
                </Link>
                <Link
                  to="/employers/login"
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-brand-violet dark:hover:text-brand-violet-on-dark transition"
                >
                  Already have an account? Sign in →
                </Link>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Questions?{" "}
                <a
                  href="mailto:support@joborigo.com"
                  className="text-brand-violet dark:text-brand-violet-on-dark hover:underline"
                >
                  support@joborigo.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
